import React, { useState } from "react";
import { KeyRound, Loader2 } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export const ChangePassword = () => {
  const [current, setCurrent] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw.length < 8) return setError("The new password must be at least 8 characters.");
    if (pw !== pw2) return setError("The two new passwords do not match.");
    if (pw === current) return setError("Choose a password different from your current one.");
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: pw });
      setCurrent(""); setPw(""); setPw2("");
      toast.success("Password updated");
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || "Could not update the password.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="change-password-card" className="bg-card border border-border rounded-xl shadow-sm">
      <div className="flex items-center gap-3 px-6 py-4 border-b border-border">
        <div className="w-9 h-9 rounded-lg bg-secondary flex items-center justify-center shrink-0">
          <KeyRound className="w-4 h-4 text-primary" />
        </div>
        <div>
          <div className="font-semibold text-slate-900 dark:text-slate-100">Password</div>
          <div className="text-xs text-muted-foreground">Change the password you use to sign in</div>
        </div>
      </div>
      <form onSubmit={submit} className="p-6 space-y-4 max-w-md">
        <div>
          <Label htmlFor="current-password">Current password</Label>
          <PasswordInput
            id="current-password"
            data-testid="current-password-input"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            placeholder="••••••••"
            required
            className="mt-1.5"
          />
        </div>
        <div>
          <Label htmlFor="change-new-password">New password</Label>
          <PasswordInput
            id="change-new-password"
            data-testid="change-new-password-input"
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            placeholder="At least 8 characters"
            required
            className="mt-1.5"
          />
        </div>
        <div>
          <Label htmlFor="change-confirm-password">Confirm new password</Label>
          <PasswordInput
            id="change-confirm-password"
            data-testid="change-confirm-password-input"
            value={pw2}
            onChange={(e) => setPw2(e.target.value)}
            placeholder="Repeat it"
            required
            className="mt-1.5"
          />
        </div>
        {error && (
          <div data-testid="change-password-error" className="text-sm text-destructive bg-destructive/10 rounded-md px-3 py-2">
            {error}
          </div>
        )}
        <Button type="submit" data-testid="change-password-button" disabled={saving} className="gap-2 font-semibold">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
          Update password
        </Button>
      </form>
    </div>
  );
};
